import React, {createContext, useContext, useEffect, useState} from 'react';
import jwtAxios from '../../@crema/services/auth/jwt-auth/jwt-api';
import {useAuthUser} from '../../@crema/utility/AuthHooks';
import {SHOW_ACTIVE_CONDITIONS} from '../conditions/ConditionsProvider';
import {useNavigate} from 'react-router-dom';

export type Quote = {
  id: string;
  column: string;
  label: string;
  type: number;
};

type QuotesContextType = {
  quotes: Quote[];
  loading: boolean;
};

interface QuotesActionsContextType {
  create: Function;
  remove: Function;
}

const defaultQuotesContext = {
  quotes: [],
  loading: false,
};

const QuotesContext = createContext<QuotesContextType>(defaultQuotesContext);

const QuotesActionsContext = createContext<QuotesActionsContextType>({
  create: () => {},
  remove: () => {},
});

export const useQuotesContext = () => useContext(QuotesContext);
export const useQuotesActionsContext = () => useContext(QuotesActionsContext);

const QuotesProvider = ({children}: any) => {
  const {user} = useAuthUser();
  const [quotes, setQuotes] = useState(defaultQuotesContext.quotes);
  const [loading, setLoading] = useState(defaultQuotesContext.loading);
  const navigate = useNavigate();

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = () => {
    setLoading(true);
    getQuotes().then(({data}) => {
      setQuotes(data.data);
      setLoading(false);
    });
  };

  const create = (column: string, label: string, type: number) => {
    setLoading(true);
    createQuote(column, label, type).then(() => {
      getQuotes().then(({data}) => {
        setQuotes(data.data);
        setLoading(false);
      });
    });
  };

  const remove = (id: string) => {
    setLoading(true);
    deleteQuote(id).then(({data}) => {
      const isConditionsHasChangedQuotes =
        data.data?.isConditionsHasChangedQuotes;
      getQuotes().then(({data}) => {
        setQuotes(data.data);
        setLoading(false);
        if (isConditionsHasChangedQuotes) {
          window.localStorage.setItem(SHOW_ACTIVE_CONDITIONS, '0');
          navigate('/conditions');
        }
      });
    });
  };

  const getQuotes = async () => {
    return jwtAxios.get(`/quote/get-all/${user.id}`);
  };

  const createQuote = async (column: string, label: string, type: number) => {
    return jwtAxios.post(`/quote/create/${user.id}`, {
      column,
      label,
      type,
    });
  };

  const deleteQuote = async (id: string) => {
    return jwtAxios.delete(`/quote/remove/${id}`);
  };

  return (
    <QuotesContext.Provider value={{quotes, loading}}>
      <QuotesActionsContext.Provider value={{create, remove}}>
        {children}
      </QuotesActionsContext.Provider>
    </QuotesContext.Provider>
  );
};

export default QuotesProvider;
